// user-client/src/state/LobbyTextFilter.js
import { useMemo } from 'react';
import { useLobbySelectors } from './LobbyDataState';

import { useLanguageState as useRuntimeLanguageState } from './RuntimeStateProvider';

/**
 * LocalizedText 지원:
 * - string
 * - { en: "...", ko: "...", pt-br: "...", es-mx: "..." ... }
 *
 * 목표:
 * - pt-br / es-mx 같은 "지역코드"는 유지
 * - 없으면 base 언어(pt, es)로 fallback
 * - ko/kr는 서로 alias 허용
 * - 그 다음 en fallback
 */
function normalizeLangTag(raw) {
  let s = String(raw || '').trim();
  if (!s) return 'en';

  s = s.replace(/_/g, '-').toLowerCase(); // pt_BR -> pt-br

  // ko-KR / ko -> ko
  if (s === 'kr') return 'ko';
  if (s.startsWith('ko')) return 'ko';

  // 혹시나 i18next가 zh-CN 같은 걸 주는 경우 대비
  return s;
}

function getLangFromLanguageState(ls) {
  if (!ls) return null;
  if (typeof ls === 'string') return ls;
  if (typeof ls.current === 'string') return ls.current;
  // ✅ current가 { code, label } 같은 객체로 오는 케이스 방어
  if (ls.current && typeof ls.current === 'object' && typeof ls.current.code === 'string') return ls.current.code;
  // ✅ RuntimeStateProvider.useLanguageState() 형태 지원: { lang: { current }, setLanguage }
  if (typeof ls.lang?.current === 'string') return ls.lang.current;
  if (ls.lang?.current && typeof ls.lang.current === 'object' && typeof ls.lang.current.code === 'string') return ls.lang.current.code;
  if (typeof ls.lang === 'string') return ls.lang;
  // [ADD][ANCHOR:LOBBY_TEXT_FILTER:LANG_NESTED_CURRENT]
  // RuntimeStateProvider의 useLanguageState() => { lang: { current } , setLanguage }
if (ls.lang && typeof ls.lang === 'object' && typeof ls.lang.current === 'string') return ls.lang.current;
  if (typeof ls.code === 'string') return ls.code;
  if (typeof ls.language === 'string') return ls.language;
  return null;
}

function buildLangCandidates(lang) {
  const l = normalizeLangTag(lang);

  // ✅ 지역코드 분리 fallback (pt-br -> pt)
  const parts = l.split('-');
  const base = parts[0];

  const out = [];

  // 1) full tag 우선
  out.push(l);

  // 2) base fallback (pt-br -> pt)
  if (base && base !== l) out.push(base);

  // 3) ko/kr alias
  if (l === 'ko') out.push('kr'); // 과거 데이터 호환

  // 4) 최종 en
  if (!out.includes('en')) out.push('en');

  // 중복 제거
  return Array.from(new Set(out.filter(Boolean)));
}

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

// [ADD][ANCHOR:LOBBY_TEXT_FILTER:COERCE_TEXT_VALUE]
function coerceTextValue(v) {
  if (v == null) return '';
  if (typeof v === 'string') {
    const parsed = tryParseJsonText(v, 3);
    if (typeof parsed === 'string') return parsed.trim();
    if (isPlainObject(parsed) || Array.isArray(parsed)) return ''; // i18n map은 상위에서 처리
    return v.trim();
  }
  if (typeof v === 'number') return String(v);
  if (isPlainObject(v)) {
    // 흔한 래핑 형태들 지원
    if (typeof v.text === 'string') return v.text.trim();
    if (typeof v.value === 'string') return v.value.trim();
    if (typeof v.label === 'string') return v.label.trim();
  }
  return '';
}

// [ADD][ANCHOR:LT:FLATTEN_NESTED_I18N]
function flattenNestedI18n(value) {
  // value(object) 내부의 "JSON 문자열" i18n 맵을 찾아서 평탄화(merge)
  if (!isPlainObject(value)) return null;
  const merged = {};

  for (const v of Object.values(value)) {
    // 1) 값이 JSON 문자열이면 파싱
    if (typeof v === 'string') {
      const parsed = tryParseJsonText(v, 2);
      if (isPlainObject(parsed)) {
        const nm = normalizeTextMap(parsed);
        if (nm) Object.assign(merged, nm);
      }
      continue;
    }

    // 2) 값이 object면 그대로 i18n map일 가능성
    if (isPlainObject(v)) {
      const nm = normalizeTextMap(v);
      if (nm) Object.assign(merged, nm);
    }
  }

  return Object.keys(merged).length ? merged : null;
}

// [ADD][ANCHOR:LOBBY_TEXT_FILTER:JSON_STRING_GUARD]
function looksLikeJsonText(s) {
  const t = String(s || '').trim();
  if (!t) return false;
  // object/array 또는 "quoted json" 형태까지 커버
  if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) return true;
  if (t.startsWith('"') && t.endsWith('"')) return true;
  return false;
}

// [PATCH][ANCHOR:LOBBY_TEXT_FILTER:SAFE_JSON_PARSE]
function tryParseJsonText(s, maxDepth = 3) {
  let cur = s;
  for (let i = 0; i < maxDepth; i++) {
    if (typeof cur !== 'string') return cur;
    const t = cur.trim();
    if (!looksLikeJsonText(t)) return null;
    try {
      cur = JSON.parse(t);
    } catch {
      return null;
    }
  }
  return cur;
}

function normalizeTextMap(obj) {
  // 키를 전부 normalize 해서 lookup 안정화 (EN, en, en-US 혼재 방지)
  if (!isPlainObject(obj)) return null;
  const out = {};
  for (const k of Object.keys(obj)) {
    const nk = normalizeLangTag(k);
       const raw = obj[k];
    // ✅ 값이 "JSON 문자열"일 수도, {text:""} 래핑일 수도 있음
    const sv = coerceTextValue(raw);
    if (sv) out[nk] = sv;
  }
  return out;
}

// [ADD][ANCHOR:LOBBY_TEXT_FILTER:COERCE_JSON_STRING]
// ✅ value가 "{...}" 또는 "\"{...}\"" 처럼 "문자열화된 JSON"이면 object로 복구
function coerceMaybeJsonObjectString(v) {
  if (typeof v !== 'string') return v;
  let s = v.trim();
  if (!s) return v;

  // 1) JSON.parse가 되는 케이스 (특히 "\"{...}\"" 같은 이중 stringify)
  for (let i = 0; i < 3; i++) {
    try {
      const parsed = JSON.parse(s);
      if (isPlainObject(parsed)) return parsed;
      if (typeof parsed === 'string') {
        s = parsed.trim();
        continue;
      }
      break;
    } catch {
      break;
    }
  }

  // 2) "{\\\"en\\\":\\\"HOT\\\"}" 처럼 백슬래시가 포함된 "깨진 JSON object 문자열" 보정
  //    -> \" 를 " 로 풀고 다시 파싱 시도
  if (/\\\"/.test(s)) {
    const fixed = s.replace(/\\\"/g, '"').replace(/\\\\/g, '\\');
    try {
      const parsed2 = JSON.parse(fixed);
      if (isPlainObject(parsed2)) return parsed2;
    } catch {}
  }

  return v;
}

/**
 * value: LocalizedText | string | number | etc
 * candidates: ['pt-br','pt','en'] 같은 우선순위
 */
export function resolveLocalizedText(value, candidates) {
  if (value == null) return '';
  const normalizedCandidates = (candidates || []).map(normalizeLangTag).filter(Boolean);
  const allowKorean = normalizedCandidates.includes('ko');
  const hasHangul = (text) => /[가-힣]/.test(String(text || ''));
  if (typeof value === 'string') {
    // ✅ 문자열인데 내용이 JSON(객체/배열/quoted-json)처럼 보이면 파싱 후 다시 해석
    const parsed = tryParseJsonText(value, 2);
    if (parsed && (isPlainObject(parsed) || Array.isArray(parsed))) {
      return resolveLocalizedText(parsed, candidates);
    }
    if (!allowKorean && hasHangul(value)) return '';
    return value;
  }
  if (typeof value === 'number') return String(value);

  if (isPlainObject(value)) {
    const m0 = normalizeTextMap(value) || {};

    // ✅ 1차: 정상 i18n map
    for (const c of normalizedCandidates) {
      const key = normalizeLangTag(c);
      if (typeof m0[key] === 'string' && m0[key]) return m0[key];
    }

    // ✅ 2차: 중첩된 JSON 문자열 i18n(map)을 풀어서 merge 후 재시도
    const nested = flattenNestedI18n(value);
    if (nested) {
      const m = { ...m0, ...nested };
      for (const c of normalizedCandidates) {
        const key = normalizeLangTag(c);
        if (typeof m[key] === 'string' && m[key]) return m[key];
      }
    }

    // ✅ 마지막 폴백: English only. Never leak another locale (especially Korean)
    // into a user who selected a different language. Korean users may still use a
    // Korean-only legacy value when that is the only localized copy available.
    if (typeof m0.en === 'string' && m0.en) return m0.en;
    if (allowKorean) {
      const ko = m0.ko || m0.kr;
      if (typeof ko === 'string' && ko) return ko;
    }
    return '';
  }

  return String(value);
}

// [FIX][ANCHOR:LOBBY_TEXT_FILTER:TABLE_NAME_FALLBACK_ANY_LOCALE_HELPER]
// 테이블 그룹 이름은 UI 번역 문구보다 운영자가 붙인 고유명사에 가깝다.
// 과거 데이터가 { ko: '...' }만 가진 경우에도 비한국어 UI에서 display_code로
// 떨어지지 않도록 선택 언어 -> en -> ko/kr -> 저장된 첫 문자열 순으로 복구한다.
function resolveTableGroupTitle(value, candidates) {
  const localized = resolveLocalizedText(value, candidates);
  if (localized) return localized;

  let raw = value;
  if (typeof raw === 'string') {
    const parsed = tryParseJsonText(raw, 3);
    if (isPlainObject(parsed)) raw = parsed;
    else if (raw.trim()) return raw.trim();
  }

  if (isPlainObject(raw)) {
    const map = normalizeTextMap(raw) || {};
    const preferred = [
      ...(candidates || []).map(normalizeLangTag),
      'en',
      'ko',
      'kr',
    ];
    for (const key of Array.from(new Set(preferred))) {
      const text = map[key];
      if (typeof text === 'string' && text.trim()) return text.trim();
    }
    const first = Object.values(map).find((text) => typeof text === 'string' && text.trim());
    if (first) return first.trim();
  }
  return '';
}

function normalizeBundle(v) {
  // LobbyDataState는 bundle 형태를 유지하지만, 혹시 array로 올 때도 대비
  if (!v) return { table_group: null, information_buttons: [] };
  if (Array.isArray(v)) {
    const tg = v.find((x) => x?.type === 'table_group') || null;
    const ibs = v.filter((x) => x && x?.type !== 'table_group');
    return { table_group: tg, information_buttons: ibs };
  }
  if (typeof v === 'object') {
    const tg = v.table_group || v.tableGroup || null;
    const ibs =
      v.information_buttons ||
      v.informationButtons ||
      v.info_buttons ||
      v.information ||
      v.information_button ||
      [];
    return { table_group: tg || null, information_buttons: Array.isArray(ibs) ? ibs : [] };
  }
  return { table_group: null, information_buttons: [] };
}

/**
 * useLobbyTextFilter
 * - lobby raw + languageState를 기준으로 "문자열 텍스트" 파생값을 만든다.
 * - lobby 갱신/언어 갱신 둘 다 자동 트리거됨(useMemo deps).
 */
export function useLobbyTextFilter() {
  const lobby = useLobbySelectors();

  // ✅ 언어 상태는 여기서만 읽는다 (너가 원한 트리거 구조)
  const ls = useRuntimeLanguageState?.();
  const rawLang =
    getLangFromLanguageState(ls) ||
    null;

  const lang = useMemo(() => normalizeLangTag(rawLang || 'en'), [rawLang]);

  const candidates = useMemo(() => buildLangCandidates(lang), [lang]);
  const candKey = useMemo(() => candidates.join('|'), [candidates]);

  // 1) table group 이름: display_code -> title string
  // [FIX][ANCHOR:LOBBY_TEXT_FILTER:TABLE_NAME_FALLBACK_ANY_LOCALE]
  const titleByCode = useMemo(() => {
    const out = {};
    const groups = Array.isArray(lobby.tableGroups) ? lobby.tableGroups : [];
    for (const g of groups) {
      const code = String(g?.display_code || g?.displayCode || '').trim();
      if (!code) continue;
      const title = resolveTableGroupTitle(g?.name, candidates);
      out[code] = title || code;
    }
    return out;
  }, [lobby.tableGroups, candKey]); // candidates 배열은 candKey로 안정화

  // 2) display set 텍스트: display_code -> { tgPoint, tgMsg, infoById }
  const displayTextByCode = useMemo(() => {
    const out = {};
    const by = lobby.displaySetsByCode && typeof lobby.displaySetsByCode === 'object'
      ? lobby.displaySetsByCode
      : {};

    for (const code of Object.keys(by)) {
      const bundle = normalizeBundle(by[code]);
      const tg = bundle.table_group || null;
      const ibs = Array.isArray(bundle.information_buttons) ? bundle.information_buttons : [];

      out[code] = {
        tableGroup: tg
          ? {
              highlightPoint: resolveLocalizedText(tg.highlight_point, candidates),
              highlightMessage: resolveLocalizedText(tg.highlight_message, candidates),
            }
          : { highlightPoint: '', highlightMessage: '' },

        // 정보버튼 텍스트는 id 기준으로 바로 찾게 만든다
        infoById: ibs.reduce((acc, ib) => {
          const id = ib?.id;
          if (id == null) return acc;
          acc[id] = {
            highlightPoint: resolveLocalizedText(ib?.highlight_point, candidates),
            highlightMessage: resolveLocalizedText(ib?.highlight_message, candidates),
          };
          return acc;
        }, {}),
      };
    }

    return out;
  }, [lobby.displaySetsByCode, candKey]);

  // 3) UI에서 그냥 t(v)로 쓰고 싶을 때(프리셋 text 등)
  const t = useMemo(() => {
    return (v) => resolveLocalizedText(v, candidates);
  }, [candKey]);

  return {
    lang,          // normalize 된 현재 언어 (ko, pt-br, es-mx 등)
    candidates,    // fallback 후보
    t,             // 공용 텍스트 resolver
    titleByCode,   // 스케줄러(2번)에서 쓰기 좋음
    displayTextByCode, // 디테일(3번)에서 highlight/ib 텍스트 가져오기 좋음
  };
}
